<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import SearchAPI from 'dashboard/api/search';
import Avatar from 'next/avatar/Avatar.vue';
import CaretAnchoredPicker from 'dashboard/components-next/preview-picker/CaretAnchoredPicker.vue';

const props = defineProps({
  caretPosition: {
    type: Object,
    default: null,
  },
  searchKey: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['selectConversation', 'close', 'removeTrigger']);

const { t } = useI18n();

const searchQuery = ref(props.searchKey);
const loading = ref(false);
const conversations = ref([]);

let requestId = 0;

const searchTerm = computed(() => searchQuery.value.trim());

async function fetchConversations(query) {
  requestId += 1;
  const id = requestId;
  loading.value = true;
  try {
    const { data } = await SearchAPI.conversations({ q: query.trim() });
    if (id !== requestId) return;
    conversations.value = data.payload?.conversations || [];
  } catch {
    if (id !== requestId) return;
    conversations.value = [];
  } finally {
    if (id === requestId) loading.value = false;
  }
}

const debouncedFetch = debounce(fetchConversations, 300, true);

watch(
  () => props.searchKey,
  value => {
    searchQuery.value = value;
  }
);

watch(searchQuery, query => debouncedFetch(query), { immediate: true });

function conversationLabel(conv) {
  return (
    conv.contact?.name ||
    `${t('INTERNAL_CHAT.CONVERSATION_MENTION.PREFIX')}${conv.id}`
  );
}

function conversationSubtitle(conv) {
  const idLabel = `${t('INTERNAL_CHAT.CONVERSATION_MENTION.PREFIX')}${conv.id}`;
  const inboxName = conv.inbox?.name;
  if (conv.contact?.name && inboxName) return `${idLabel} · ${inboxName}`;
  if (conv.contact?.name) return idLabel;
  return inboxName || '';
}

const items = computed(() =>
  conversations.value.map(conv => ({
    id: conv.id,
    record: {
      id: conv.id,
      type: 'conversation',
      displayName: String(conv.id),
      name: String(conv.id),
      contactName: conv.contact?.name || '',
      inboxName: conv.inbox?.name || '',
      assigneeName: conv.agent?.available_name || conv.agent?.name || '',
      message: conv.message?.content || '',
    },
    label: conversationLabel(conv),
    title: conversationLabel(conv),
    subtitle: conversationSubtitle(conv),
  }))
);

const emptyLabel = computed(() => {
  if (searchTerm.value) {
    return t('COMBOBOX.EMPTY_SEARCH_RESULTS', { searchTerm: searchTerm.value });
  }
  return t('INTERNAL_CHAT.CONVERSATION_MENTION.TYPE_TO_SEARCH');
});

const onSelect = item => emit('selectConversation', item.record);
</script>

<template>
  <CaretAnchoredPicker
    v-model:search="searchQuery"
    :caret-position="caretPosition"
    :items="items"
    :search-placeholder="
      t('CONVERSATION.PICKER.CONVERSATION.SEARCH_PLACEHOLDER')
    "
    :hint="t('CONVERSATION.PICKER.HINT')"
    :is-loading="loading"
    :empty-label="emptyLabel"
    @select="onSelect"
    @close="emit('close')"
    @remove-trigger="emit('removeTrigger')"
  >
    <template #leading="{ item }">
      <Avatar
        :name="item.label"
        :size="24"
        rounded-full
        class="flex-shrink-0"
      />
    </template>
    <template #preview="{ item }">
      <div v-if="item" class="flex flex-col gap-3 px-4 py-3">
        <div class="flex items-start gap-3">
          <Avatar
            :name="item.label"
            :size="40"
            rounded-full
            class="flex-shrink-0"
          />
          <div class="flex flex-col min-w-0 gap-0.5">
            <span class="text-sm font-medium truncate text-n-slate-12">
              {{ item.label }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ item.subtitle }}
            </span>
          </div>
        </div>
        <div class="flex flex-col gap-2 text-xs">
          <div
            v-if="item.record.inboxName"
            class="flex items-center justify-between gap-2"
          >
            <span class="text-n-slate-10">
              {{ t('CONVERSATION.PICKER.CONVERSATION.INBOX') }}
            </span>
            <span class="truncate text-n-slate-12">{{
              item.record.inboxName
            }}</span>
          </div>
          <div class="flex items-center justify-between gap-2">
            <span class="text-n-slate-10">
              {{ t('CONVERSATION.PICKER.CONVERSATION.ASSIGNEE') }}
            </span>
            <span class="truncate text-n-slate-12">
              {{
                item.record.assigneeName ||
                t('CONVERSATION.PICKER.CONVERSATION.UNASSIGNED')
              }}
            </span>
          </div>
          <p
            v-if="item.record.message"
            class="pt-1 leading-relaxed line-clamp-4 text-n-slate-11"
          >
            {{ item.record.message }}
          </p>
        </div>
      </div>
    </template>
  </CaretAnchoredPicker>
</template>
